import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useEffect, useRef, useState } from 'react'
import { usePeople } from '../hooks/usePeople'
import { PixeCard } from '../components/ui/PixeCard'
import ErrorBoundary from '../components/common/ErrorBoundary'
import NavBar from '../components/layout/NavBar'
import { getToken } from '../lib/auth'
import { createBroadcastRequest } from '../api/prosRequests'
import PeopleFilterDrawer from '../components/people/PeopleFilterDrawer'
import type { PeopleFilters } from '../types/people'
import { DEFAULT_PEOPLE_FILTERS, isDefaultPeopleFilters } from '../types/people'
import { fetchPeopleFeaturedFiltered } from '../api/people'
import MobilePeopleActionBar from '../components/pixe/MobilePeopleActionBar'
import CommentsDrawer from '../components/comments/CommentsDrawer'

type PeopleCard = {
  displayName: string
  handle: string
  avatar: string
  coverImage?: string
  motto?: string
  verified?: boolean
  public_id: string
  shareUrl?: string
}

export default function People() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const qParam = searchParams.get('q') || ''
  const [q, setQ] = useState(qParam)
  useEffect(() => { setQ(qParam) }, [qParam])
  const [sort, setSort] = useState<'trending' | 'newest'>('trending')
  const [verified, setVerified] = useState<boolean>(false)
  const [activeChip, setActiveChip] = useState<'all' | 'trending' | 'newest' | 'verified'>('all')
  const [filterOpen, setFilterOpen] = useState(false)
  const [filters, setFilters] = useState<PeopleFilters>({ ...DEFAULT_PEOPLE_FILTERS, sort })
  useEffect(() => { setFilters((p) => ({ ...p, sort })) }, [sort])
  const { items, error, loading, debouncedQuery, filterSignature, lastFetchUrl, lastSuccessAt } = usePeople(q, { sort, verifiedOnly: verified, filters })
  const debugSearch = import.meta.env.DEV && searchParams.get('debugSearch') === '1'
  const [trending, setTrending] = useState<PeopleCard[] | null>(null)
  // Disable the extra trending rail to avoid duplicate rows; main grid handles listing.
  const showTrendingRail = false
  const isAllActive = !verified && sort === 'trending' && isDefaultPeopleFilters(filters) && !(q && q.trim())

  const dedupe = (arr: PeopleCard[] | null | undefined) => {
    if (!arr) return arr
    const seen = new Map<string, PeopleCard>()
    for (const p of arr) {
      if (!p?.public_id || String(p.public_id).toLowerCase() === 'undefined') continue
      const key = p.public_id ?? `${p.handle}-${p.displayName}`
      if (key && !seen.has(String(key))) seen.set(String(key), p)
    }
    return Array.from(seen.values())
  }

  const onResultsRenderError = (error: Error) => {
    const first = dedupe(items)?.[0]
    let firstPreview = ''
    try {
      firstPreview = first ? JSON.stringify(first).slice(0, 500) : ''
    } catch {
      firstPreview = '[unserializable]'
    }
    console.error('PeopleResultsRenderError', {
      message: error?.message,
      stack: error?.stack,
      q,
      firstResult: firstPreview,
    })
  }

  // Keep a small rail ready; hide when filters are active to avoid confusion
  useEffect(() => {
    if (!showTrendingRail) { setTrending(null); return }
    let cancelled = false
    async function load() {
      try {
        const arr = await fetchPeopleFeaturedFiltered({ limit: 12, filters })
        const res = verified ? arr.filter((p: any) => p?.verified) : arr
        if (!cancelled) setTrending(dedupe(res) || null)
      } catch { if (!cancelled) setTrending(null) }
    }
    load()
    return () => { cancelled = true }
  }, [q, JSON.stringify(filters), verified, showTrendingRail])

  // Mobile fullscreen people feed state (TikTok mode).
  const mobileFeedRef = useRef<HTMLDivElement | null>(null)
  const tapRef = useRef<{ x: number; y: number; moved: boolean; t: number } | null>(null)
  const [commentsOpen, setCommentsOpen] = useState(false)
  const [commentsProfileId, setCommentsProfileId] = useState<string | null>(null)
  const [requestModalOpen, setRequestModalOpen] = useState(false)
  const [requestBusy, setRequestBusy] = useState(false)
  const [requestError, setRequestError] = useState<string | null>(null)
  const [requestSummary, setRequestSummary] = useState('')
  const [requestLocationText, setRequestLocationText] = useState('')
  const [requestZipcode, setRequestZipcode] = useState('')
  const [requestUrgency, setRequestUrgency] = useState<'FLEXIBLE' | 'TODAY' | 'ASAP'>('FLEXIBLE')
  const [requestScheduledFor, setRequestScheduledFor] = useState('')
  const [requestSkills, setRequestSkills] = useState('')

  useEffect(() => {
    const sp = new URLSearchParams(location.search)
    if (sp.get('filters') === '1') setFilterOpen(true)
  }, [location.search])

  useEffect(() => {
    const open = () => setFilterOpen(true)
    window.addEventListener('pixeloop:peopleFiltersOpen' as any, open)
    return () => window.removeEventListener('pixeloop:peopleFiltersOpen' as any, open)
  }, [])

  useEffect(() => {
    const onSearchInput = (event: Event) => {
      const next = String((event as CustomEvent)?.detail?.q ?? '').trim()
      setQ(next)
      const sp = new URLSearchParams(location.search)
      const current = (sp.get('q') || '').trim()
      if (next === current) return
      if (next) sp.set('q', next)
      else sp.delete('q')
      navigate(`${location.pathname}${sp.toString() ? `?${sp.toString()}` : ''}`, { replace: true })
    }
    window.addEventListener('pixeloop:peopleSearchInput' as any, onSearchInput as any)
    return () => window.removeEventListener('pixeloop:peopleSearchInput' as any, onSearchInput as any)
  }, [location.pathname, location.search, navigate])

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (!qParam) return
      const next = new URLSearchParams(location.search)
      next.delete('q')
      navigate(`${location.pathname}${next.toString() ? `?${next.toString()}` : ''}`, { replace: true })
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [qParam, location.pathname, location.search, navigate])

  // Restore mobile scroll after returning from profile reader.
  useEffect(() => {
    const root = mobileFeedRef.current
    if (!root) return
    if (!items) return
    try {
      const raw = sessionStorage.getItem('mobilePeople:scrollTop')
      if (!raw) return
      const top = Number(raw)
      if (!Number.isFinite(top)) return
      root.scrollTop = top
      sessionStorage.removeItem('mobilePeople:scrollTop')
    } catch {}
  }, [items])

  useEffect(() => {
    if (filters.zipcode && !requestZipcode) setRequestZipcode(filters.zipcode)
  }, [filters.zipcode, requestZipcode])

  async function onCreateBroadcastRequest() {
    if (!getToken()) {
      navigate('/login')
      return
    }
    if (!requestSummary.trim()) {
      setRequestError('Summary is required.')
      return
    }
    if (!requestLocationText.trim()) {
      setRequestError('Location is required.')
      return
    }
    if (!requestZipcode.trim()) {
      setRequestError('ZIP code is required.')
      return
    }
    try {
      setRequestBusy(true)
      setRequestError(null)
      const res = await createBroadcastRequest({
        summary: requestSummary.trim(),
        client_location_text: requestLocationText.trim(),
        client_zipcode: requestZipcode.trim(),
        urgency: requestUrgency,
        scheduled_for: requestScheduledFor ? new Date(requestScheduledFor).toISOString() : undefined,
        skills: requestSkills
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
      })
      setRequestModalOpen(false)
      navigate(`/jobs/${res.job_id}`)
      const ttl = Number(res.delivery_ttl_hours || 24)
      window.alert(`Sent to ${res.matched_count || 0} Pros in your area. Request expires in ${ttl} hours.`)
    } catch (e: any) {
      setRequestError(e?.response?.data?.detail || e?.response?.data?.code || e?.message || 'Failed to create local request')
    } finally {
      setRequestBusy(false)
    }
  }

  return (
    <div className="min-h-screen">
      <NavBar mobileFixed />

      {/* Phone mode (< md): TikTok-style fullscreen People feed */}
      <div className="md:hidden fixed inset-0 bg-black">
        {/* Keep mobile header actions minimal; hide filter controls on small screens. */}
        <div
          className="fixed right-3 z-30"
          style={{
            top: 'calc(var(--nav-h, 56px) + 10px)',
          }}
        >
          <div className="flex items-center gap-2">
            <Link
              to="/pros/discover"
              className="inline-flex items-center rounded-full bg-brand px-3 py-2 text-xs font-semibold text-white hover:bg-brand-600"
            >
              Browse Pros
            </Link>
          </div>
        </div>

        <div
          ref={mobileFeedRef}
          className="h-[100dvh] w-full overflow-y-auto snap-y snap-mandatory overscroll-contain no-scrollbar"
        >
          <ErrorBoundary
            name="PeopleMobileResults"
            onError={onResultsRenderError}
            fallback={<div className="snap-start h-[100dvh] w-full relative bg-neutral-950 flex items-center justify-center px-6"><div className="text-sm text-white/80">Something went wrong loading results. Please refresh.</div></div>}
          >
            {(dedupe(items) ?? []).map((p, i) => {
              const hero = p.coverImage || p.avatar || null
              const href = `/people/i/${p.public_id}`
              return (
                <div
                  key={p.public_id ?? i}
                  className="snap-start h-[100dvh] w-full relative bg-black"
                  data-feed-item="1"
                  data-index={i}
                  onPointerDown={(e) => {
                    const t = e.target as HTMLElement
                    if (t?.closest?.('button,a,input,textarea,select,label,[role=\"button\"],[data-no-feed-tap=\"1\"]')) return
                    tapRef.current = { x: e.clientX, y: e.clientY, moved: false, t: Date.now() }
                  }}
                  onPointerMove={(e) => {
                    const s = tapRef.current
                    if (!s) return
                    const dx = Math.abs(e.clientX - s.x)
                    const dy = Math.abs(e.clientY - s.y)
                    if (dx > 12 || dy > 12) s.moved = true
                  }}
                  onPointerUp={(e) => {
                    const s = tapRef.current
                    tapRef.current = null
                    if (!s) return
                    const dt = Date.now() - s.t
                    if (s.moved || dt > 650) return
                    const t = e.target as HTMLElement
                    if (t?.closest?.('button,a,input,textarea,select,label,[role=\"button\"],[data-no-feed-tap=\"1\"]')) return
                    try {
                      if (mobileFeedRef.current) sessionStorage.setItem('mobilePeople:scrollTop', String(mobileFeedRef.current.scrollTop))
                    } catch {}
                    navigate(href, { state: { fromMobilePeople: true } as any })
                  }}
                >
                  {hero ? (
                    // eslint-disable-next-line jsx-a11y/img-redundant-alt
                    <img src={hero} alt={`${p.displayName} cover image`} className="absolute inset-0 h-full w-full object-cover" />
                  ) : (
                    <div className="absolute inset-0 bg-white/5" />
                  )}

                  <div className="absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-black/55 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 h-56 bg-gradient-to-t from-black/85 to-transparent" />

                  <div className="absolute left-0 right-0 bottom-0 p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
                    <div className="max-w-[75%]">
                      <div className="text-xl font-extrabold leading-tight drop-shadow">{p.displayName || 'Creator'}</div>
                      <div className="mt-1 text-sm text-white/85 drop-shadow">{p.handle ? (p.handle.startsWith('@') ? p.handle : `@${p.handle}`) : ''}</div>
                      {p.motto ? <div className="mt-2 text-sm text-white/80 max-h-10 overflow-hidden drop-shadow">{p.motto}</div> : null}
                      <div className="mt-3 inline-flex items-center rounded-full bg-white/10 border border-white/10 px-3 py-1.5 text-xs text-white/90 backdrop-blur">
                        Tap to open profile
                      </div>
                    </div>
                  </div>

                  <MobilePeopleActionBar
                    publicId={p.public_id}
                    avatarUrl={p.avatar || null}
                    shareUrl={p.shareUrl || null}
                    onOpenProfile={() => navigate(href)}
                    onOpenComments={() => {
                      setCommentsProfileId(p.public_id)
                      setCommentsOpen(true)
                    }}
                    onOpenGift={() => navigate(href, { state: { openGift: true } as any })}
                  />
                </div>
              )
            })}
          </ErrorBoundary>
          {!items && !error && (
            <div className="snap-start h-[100dvh] w-full relative bg-neutral-950">
              <div className="absolute inset-0 bg-[radial-gradient(50%_50%_at_50%_20%,rgba(99,102,241,0.18),transparent_70%)]" />
              <div className="absolute left-0 right-0 bottom-0 p-6">
                <div className="glass rounded-2xl h-24" />
              </div>
            </div>
          )}
          {error && (
            <div className="snap-start h-[100dvh] w-full relative bg-neutral-950 flex items-center justify-center px-6">
              <div className="text-sm text-red-300">{error}</div>
            </div>
          )}
        </div>
      </div>

      {commentsProfileId ? (
        <CommentsDrawer
          open={commentsOpen}
          onClose={() => setCommentsOpen(false)}
          target={{ type: 'person', id: commentsProfileId }}
        />
      ) : null}

      {/* Desktop + iPad (>= md): unchanged */}
      <main className="hidden md:block mx-auto max-w-6xl px-4 py-6">
        {/* chips row */}
        <div className="mb-4">
          <div className="flex items-center justify-between gap-2">
          <div className="flex gap-2 overflow-x-auto whitespace-nowrap">
            {['All','Trending','Newest','Verified','Pros Only'].map((c) => (
                <button
                  key={c}
                  onClick={() => {
                    if (c === 'Trending') {
                      setSort('trending')
                      setActiveChip('trending')
                    } else if (c === 'Newest') {
                      setSort('newest')
                      setActiveChip('newest')
                    } else if (c === 'Verified') {
                      setVerified((v) => {
                        const next = !v
                        setActiveChip(next ? 'verified' : (sort === 'newest' ? 'newest' : 'trending'))
                        return next
                      })
                    } else if (c === 'Pros Only') {
                      setFilters((prev) => ({ ...prev, prosOnly: !prev.prosOnly }))
                    } else {
                      // All: reset filters/sort to defaults
                      setSort('trending')
                      setVerified(false)
                      setFilters({ ...DEFAULT_PEOPLE_FILTERS, sort: 'trending' })
                      setActiveChip('all')
                    }
                  }}
                className={`px-2.5 py-1 text-xs md:text-sm rounded-full border ${
                  (c==='All' && activeChip==='all') ||
                  (c==='Trending' && activeChip==='trending') ||
                  (c==='Newest' && activeChip==='newest') ||
                  (c==='Verified' && verified) ||
                  (c==='Pros Only' && filters.prosOnly)
                    ? 'bg-brand border-transparent'
                    : 'bg-white/5 border-white/10'
                }`}
              >
                {c}
              </button>
            ))}
          </div>
            <div className="flex items-center gap-2 shrink-0">
              <Link
                to="/pros/discover"
                className="rounded-full bg-brand px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-600"
              >
                Browse Pros
              </Link>
              {filters.prosToGo ? (
                <button
                  type="button"
                  onClick={() => setRequestModalOpen(true)}
                  className="rounded-full border border-white/20 bg-white/5 px-3 py-1.5 text-xs hover:bg-white/10"
                >
                  Request Pros to Go
                </button>
              ) : null}
              <button
                type="button"
                aria-label="Filters"
                onClick={() => setFilterOpen(true)}
                className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-white/15 bg-white/5 hover:bg-white/10"
                title="Filter People"
              >
                <svg viewBox="0 0 24 24" className="h-4 w-4 fill-white/85" aria-hidden="true">
                  <path d="M3 4h18l-7.5 9v5.5l-3 1.5V13L3 4z"/>
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* (CTA will appear in the Trending People header row below) */}

        {/* Trending People rail */}
        {showTrendingRail && (
          <section className="mb-6">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-lg font-extrabold">Trending People</h3>
              <div className="inline-flex items-center gap-2">
                <button
                  type="button"
                  aria-label="Filters"
                  onClick={() => setFilterOpen(true)}
                  className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-white/15 bg-white/5 hover:bg-white/10"
                  title="Filter People"
                >
                  <svg viewBox="0 0 24 24" className="h-4 w-4 fill-white/85" aria-hidden="true">
                    <path d="M3 4h18l-7.5 9v5.5l-3 1.5V13L3 4z"/>
                  </svg>
                </button>
                <Link
                  to={getToken() ? '/people/edit' : '/signup'}
                  className="inline-flex items-center gap-1.5 rounded-full bg-brand px-3 py-1.5 text-xs md:text-sm font-semibold text-white shadow hover:bg-brand-600"
                >
                  <svg viewBox="0 0 24 24" className="h-4 w-4 text-white" aria-hidden="true">
                    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25z" fill="currentColor" />
                  </svg>
                  {getToken() ? 'Edit' : 'Sign up'}
                </Link>
              </div>
            </div>
            {trending?.length ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {trending.map((p, i) => (
                  <PixeCard
                    key={p.public_id ?? i}
                    title={p.displayName}
                    subtitle={p.handle}
                    image={p.coverImage || null}
                    href={`/people/i/${p.public_id}`}
                    watermarkLabel="PEOPLE"
                  />
                ))}
              </div>
            ) : (
              <div className="text-sm text-white/70">No people match these filters.</div>
            )}
          </section>
        )}

        {loading ? <div className="mb-2 text-xs text-white/60">Searching...</div> : null}
        {error ? <div className="mb-2 text-sm text-red-400">Couldn't load results. Showing last available results.</div> : null}
        {debugSearch ? (
          <div className="mb-3 rounded-lg border border-white/10 bg-black/30 p-2 text-[11px] text-white/70 space-y-1">
            <div><span className="text-white/50">query:</span> {q || '(empty)'}</div>
            <div><span className="text-white/50">debounced:</span> {debouncedQuery || '(empty)'}</div>
            <div><span className="text-white/50">loading:</span> {String(loading)}</div>
            <div><span className="text-white/50">results:</span> {dedupe(items)?.length || 0}</div>
            <div><span className="text-white/50">sig:</span> {filterSignature}</div>
            <div><span className="text-white/50">url:</span> {lastFetchUrl || '-'}</div>
            <div><span className="text-white/50">last success:</span> {lastSuccessAt ? new Date(lastSuccessAt).toLocaleTimeString() : '-'}</div>
          </div>
        ) : null}

        {/* grid of people cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {!items && !error && Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="rounded-xl bg-white/5 h-56" />
          ))}
          <ErrorBoundary
            name="PeopleDesktopResults"
            onError={onResultsRenderError}
            fallback={<div className="text-sm text-white/80">Something went wrong loading results. Please refresh.</div>}
          >
            {dedupe(items)?.map((p, i) => (
              <PixeCard
                key={p.public_id ?? i}
                title={p.displayName}
                subtitle={p.handle}
                image={p.coverImage || null}
                href={`/people/i/${p.public_id}`}
                watermarkLabel="PEOPLE"
              />
            ))}
          </ErrorBoundary>
        </div>
      </main>
        <PeopleFilterDrawer
          open={filterOpen}
          onClose={() => setFilterOpen(false)}
          filters={filters}
          onApply={(next) => { setFilters(next); setSort(next.sort); setFilterOpen(false) }}
          onClear={() => { setFilters({ ...DEFAULT_PEOPLE_FILTERS, sort: 'trending' }); setSort('trending') }}
        />
      {requestModalOpen ? (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/70" onClick={() => setRequestModalOpen(false)} />
          <div className="absolute inset-x-0 top-8 mx-auto w-[95%] max-w-lg rounded-2xl border border-white/10 bg-neutral-900 p-4 shadow-2xl space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-lg font-semibold">Request Pros to Go</div>
              <button type="button" onClick={() => setRequestModalOpen(false)} className="text-white/70 hover:text-white">Close</button>
            </div>
            {requestError ? <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-2 text-sm text-red-200">{requestError}</div> : null}
            <label className="block text-sm">Summary
              <textarea value={requestSummary} onChange={(e) => setRequestSummary(e.target.value)} rows={3} className="mt-1 w-full rounded-lg border border-white/15 bg-black px-3 py-2 text-sm" />
            </label>
            <label className="block text-sm">Location text
              <input value={requestLocationText} onChange={(e) => setRequestLocationText(e.target.value)} className="mt-1 w-full rounded-lg border border-white/15 bg-black px-3 py-2 text-sm" placeholder="Julian, CA" />
            </label>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="block text-sm">ZIP code
                <input value={requestZipcode} onChange={(e) => setRequestZipcode(e.target.value)} className="mt-1 w-full rounded-lg border border-white/15 bg-black px-3 py-2 text-sm" placeholder="92036" />
              </label>
              <label className="block text-sm">Urgency
                <select value={requestUrgency} onChange={(e) => setRequestUrgency(e.target.value as any)} className="mt-1 w-full rounded-lg border border-white/15 bg-black px-3 py-2 text-sm">
                  <option value="FLEXIBLE">Flexible</option>
                  <option value="TODAY">Today</option>
                  <option value="ASAP">ASAP</option>
                </select>
              </label>
            </div>
            <label className="block text-sm">Scheduled for (optional)
              <input type="datetime-local" value={requestScheduledFor} onChange={(e) => setRequestScheduledFor(e.target.value)} className="mt-1 w-full rounded-lg border border-white/15 bg-black px-3 py-2 text-sm" />
            </label>
            <label className="block text-sm">Skills (optional)
              <input value={requestSkills} onChange={(e) => setRequestSkills(e.target.value)} className="mt-1 w-full rounded-lg border border-white/15 bg-black px-3 py-2 text-sm" placeholder="plumbing, electrician" />
            </label>
            <div className="flex justify-end">
              <button type="button" onClick={onCreateBroadcastRequest} disabled={requestBusy} className="rounded-xl bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-60">
                {requestBusy ? 'Sending...' : 'Send Request'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}





